export type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "forbidden" | "error";

export interface AuthUser {
  id: number | string;
  sso_id: string;
  name: string;
  email: string;
  avatar: string | null;
  status: string;
}

export interface OrganizationMembership {
  id: number | string;
  sso_id: string;
  name: string;
  organization_status: string;
  role: string | null;
  membership_status: string;
  is_active?: boolean;
}

export interface WorkspaceContext {
  user: AuthUser;
  active_organization: OrganizationMembership | null;
  organizations: OrganizationMembership[];
}

export interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T | null;
  meta?: Record<string, unknown>;
  errors?: Array<{ code?: string; message?: string; [key: string]: unknown }>;
}

export class AuthApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errors: ApiEnvelope<null>["errors"] = [],
  ) {
    super(message);
    this.name = "AuthApiError";
  }
}

export async function getAuthContext(signal?: AbortSignal): Promise<WorkspaceContext> {
  const response = await fetch("/api/auth/me", {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "include",
    cache: "no-store",
    signal,
  });

  const payload = (await response.json()) as ApiEnvelope<WorkspaceContext>;

  if (!response.ok || !payload.success || !payload.data) {
    throw new AuthApiError(payload.message || "Unable to authenticate the user.", response.status, payload.errors);
  }

  return payload.data;
}

export async function switchActiveOrganization(ssoOrganizationId: string): Promise<WorkspaceContext> {
  const response = await fetch("/api/auth/active-organization", {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    cache: "no-store",
    body: JSON.stringify({ sso_organization_id: ssoOrganizationId }),
  });

  const payload = (await response.json()) as ApiEnvelope<WorkspaceContext>;

  if (!response.ok || !payload.success || !payload.data) {
    throw new AuthApiError(payload.message || "Unable to switch organization.", response.status, payload.errors);
  }

  return payload.data;
}
